import { Component, Input, OnChanges } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FaviconSwitchComponent } from './favicon-switch.component';

const dict = {
  passwordPlaceholder: '新しいパスワード',
  passwordConfirmationPlaceholder: '新しいパスワード(確認)',
  passwordConfirmationMatchError:
    '新しいパスワードが一致しません。確認してください。',
  passwordStrength: {
    containsAtLeast:
      '文字の種類が最低 %d 種類必要となります。以下の %d つの文字種類から入力してください:',
    identicalChars:
      '連続して％d個の同一の文字を使用できません（例： "%s" は許可されていません）',
    nonEmpty: 'パスワードが入力されていません',
    numbers: '半角数字 (i.e. 0-9)',
    lengthAtLeast: '%d 文字以上',
    lowerCase: '半角小文字英字 (a-z)',
    shouldContain: '以下を含む必要があります。:',
    specialCharacters: '特殊文字 (e.g. !@#$%^&*)',
    upperCase: '半角大文字英字 (A-Z)',
  },
  successMessage: 'パスワードのリセットが成功しました。',
  configurationError:
    'エラーが発生しました。 フォームに誤った設定があるようです。',
  networkError: 'サーバーにアクセスできない、ネットワークに問題があります。',
  timeoutError: 'サーバーにアクセスできない場合は、もう一度お試しください。',
  serverError: 'パスワードのリセット処理中にエラーが発生しました。',
  headerText: '',
  title: '',
  weakPasswordError:
    '半角英数字と記号を3種類以上含めた、8文字以上のパスワードを入力してください',
  passwordHistoryError: 'このパスワードは以前使用されています。',
};

@Component({
  selector: 'app-base-reset-password',
  templateUrl: './base-reset-password.component.html',
  styleUrls: ['./base-reset-password.component.css'],
})
export class BaseResetPasswordComponent
  extends FaviconSwitchComponent
  implements OnChanges
{
  password: string;
  confirmationPassword: string;

  errorMessageDict = {
    passwordNotInput: 'パスワードを入力してください。',
    confirmPasswordNotInput: '確認用パスワードを入力してください。',
    passwordNotMatch: 'パスワードが一致しません。',
  };

  errorMessages = {
    password: '',
    confirmPassword: '',
  };

  @Input()
  _csrf: string;
  @Input()
  ticket: string;
  @Input()
  email: string;

  async submitChangePassword() {
    this.errorMessages = {
      password: '',
      confirmPassword: '',
    };

    if (!this.password) {
      this.errorMessages.password = this.errorMessageDict.passwordNotInput;
    }
    if (!this.confirmationPassword) {
      this.errorMessages.confirmPassword =
        this.errorMessageDict.confirmPasswordNotInput;
    } else if (this.password !== this.confirmationPassword) {
      this.errorMessages.confirmPassword =
        this.errorMessageDict.passwordNotMatch;
    }
    if (this.errorMessages.password || this.errorMessages.confirmPassword) {
      return;
    }

    // 異常事態
    if (!this._csrf) return;
    if (!this.ticket) return;
    if (!this.email) return;

    try {
      const password = encodeURIComponent(this.password);
      const confirmationPassword = encodeURIComponent(
        this.confirmationPassword
      );
      const result = await fetch('/lo/reset', {
        headers: {
          accept: '*/*',
          'accept-language': 'ja',
          'content-type': 'application/x-www-form-urlencoded',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'same-origin',
        },
        body: `newPassword=${password}&confirmNewPassword=${confirmationPassword}&_csrf=${this._csrf}&ticket=${this.ticket}`,
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
      });
      // Auth0の管理API経由で作成した場合のレスポンスの考慮
      const response = await this.handlingWhenHtmlResponse(result);

      const json = JSON.parse(response);
      if (json.statusCode >= 400) {
        throw json;
      }
      // 成功したらresult_urlへリダイレクト
      if (json.success) {
        const customRedirectUrl = this.app?.customUrls?.onUpdatePasswordFinish;
        // envに指定があった場合はそちらへ
        if (customRedirectUrl) {
          location.href = customRedirectUrl;
        } else {
          // そうでなければ共通設定 /verify/client_id
          location.href = json.result_url;
        }
      }
    } catch (error) {
      const passwordErrors = {
        PasswordStrengthError: 'weakPasswordError',
        PasswordHistoryError: 'passwordHistoryError',
        PasswordDictionaryError: 'passwordDictError',
        PasswordNoUserInfoError: 'passwordNoUserInfoError',
      };

      const msg = dict[passwordErrors[error.name]];
      this.errorMessages.confirmPassword = msg;
    }
  }

  /**
   * https://github.com/DoctorsInc/doctorsid_backend/pull/152
   * https://valueonepj.backlog.com/view/LA_SUITE-65
   * 上記対応にて、Auth0の管理API経由で作成した場合のパスワードリセットページでPOSTリクエストした場合はJSONではなくHTMLが返却されることが判明した
   * そのため、JSON.parseでエラーが発生するため、text()で取得し、JSON.parseできるかどうかで判定する
   * パースに失敗した場合は通常とどうようのリダイレクト処理を行う。エラーが発生した場合は通常通りJSONで返るため影響なし
   */
  private async handlingWhenHtmlResponse(result: Response) {
    const response = await result.text();
    try {
      JSON.parse(response);
    } catch (error) {
      const customRedirectUrl = this.app?.customUrls?.onUpdatePasswordFinish;
      // envに指定があった場合はそちらへ
      if (customRedirectUrl) {
        location.href = customRedirectUrl;
      } else {
        // そうでなければ共通設定 /verify/client_id
        location.href = `${environment.baseUri}/verify/${this.clientId}`;
      }
    }
    return response;
  }

  get baseUri() {
    return environment.baseUri;
  }
}
